/**
 * @format
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import {
    GenericPlanEducationOption,
    PlanStudyRightService,
    PlanValidationResult,
    PlanValidationTs,
    ValidatablePlan,
} from 'common-typescript';
import {
    CourseUnit,
    CourseUnitSelection,
    CustomStudyDraft,
    Education,
    EntityWithRule,
    Module,
    ModuleContentApplication,
    ModuleContentWorkflow,
    OtmId,
    StudyRight,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import {
    combineLatestWith,
    EMPTY,
    exhaustMap,
    map,
    merge,
    mergeMap,
    Observable,
    of,
    Subject,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs';
import {
    AppErrorHandler,
    confirmDialogFdOpener,
    ConfirmDialogFdValues,
    CourseUnitEntityService,
    findMatchingStudyRightIgnoringState,
    GradeScaleEntityService,
    PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
    PlanActionsService,
    PlanData,
    PlanDiffService,
    PlanEntityService,
    PlanManager,
    PlanRuleData,
    PlanRuleDataService,
    PlanStateObject,
    PlanStateService,
    RuleClearSignalService,
    RuleError,
    RuleErrorState,
    RuleErrorStateService,
    ScrollService,
    sisuChain,
    UiOperation,
    UiOperationType,
    WorkflowEntityService,
} from 'sis-components';
import { applyForModuleContentWorkflowDialogOpener } from '../../../../plan-ng/apply-for-module-content-workflow-dialog/apply-for-module-content-workflow-dialog.component';
import { moduleContentWorkflowDialogOpener } from '../../../../plan-ng/module-content-workflow-dialog/module-content-workflow-dialog.component';
import { DialogActions } from '../../../service/attainment-student-modal-service';
import { StudentPlanActionsService } from '../../../service/student-plan-actions.service';
import { StudentPlanUiActionsServiceImplementation } from '../../../service/student-plan-ui-actions-service-implementation.service';
import { ShowViewModuleContentWorkflowLinkService } from '../../show-view-module-content-application-link/show-view-module-content-workflow-link.service';
import { PlanStructureEditInvalidSelectionsComponent } from './plan-structure-edit-invalid-selections/plan-structure-edit-invalid-selections.component';
import { PlanStructureFreeEditComponent } from './plan-structure-free-edit/plan-structure-free-edit.component';

export function planStructureEditModalOpener(): (
    modalValues: PlanStructureEditModalValues & { handleOpenAttainmentDetailsModalUiOperation?: (courseUnit: CourseUnit) => void },
) => MatDialogRef<PlanStructureEditModalComponent, { data: object }> {
    const fudisDialogService = inject(FudisDialogService);
    return modalValues => fudisDialogService.open(PlanStructureEditModalComponent, { data: modalValues });
}

export interface PlanStructureEditModalValues {
    module: EntityWithRule;
    validatablePlan: ValidatablePlan;
    validatablePlanStudyRight: StudyRight;
    planValidationResult: PlanValidationResult;
    education: Education;
    allStudentStudyRights: StudyRight[];
    dismissFocusTarget?: HTMLElement;
}

export interface PlanStructureEditModalData {
    planData: PlanData;
    planStateObject: PlanStateObject;
    planValidationResult: PlanValidationResult;
    planRuleData: PlanRuleData;
    validatablePlan: ValidatablePlan;
    selectedModule: EntityWithRule;
}

@Component({
    selector: 'app-plan-structure-edit-modal',
    templateUrl: './plan-structure-edit-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
            useClass: StudentPlanActionsService,
        },
        PlanManager,
        RuleClearSignalService,
        RuleErrorStateService,
    ],
    standalone: false,
})
export class PlanStructureEditModalComponent implements OnInit, OnDestroy {
    modalValues: PlanStructureEditModalValues = inject(MAT_DIALOG_DATA);
    private confirmDialogFdOpener = confirmDialogFdOpener();
    private planManager: PlanManager = inject(PlanManager);
    private planActionsService: PlanActionsService = inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN);
    private planStateService: PlanStateService = inject(PlanStateService);
    private planRuleDataService: PlanRuleDataService = inject(PlanRuleDataService);
    protected ruleErrorStateService: RuleErrorStateService = inject(RuleErrorStateService);
    private gradeScaleEntityService: GradeScaleEntityService = inject(GradeScaleEntityService);
    private planEntityService: PlanEntityService = inject(PlanEntityService);
    private dialogService = inject(FudisDialogService);
    private appErrorHandler = inject(AppErrorHandler);
    private destroyRef: DestroyRef = inject(DestroyRef);
    private studentPlanUIActionsImplementationService = inject(StudentPlanUiActionsServiceImplementation);
    private planDiffService = inject(PlanDiffService);
    private courseUnitEntityService = inject(CourseUnitEntityService);
    private readonly scrollService = inject(ScrollService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly moduleContentWorkflowDialogOpener = moduleContentWorkflowDialogOpener();
    private readonly applyForModuleContentWorkflowDialogOpener = applyForModuleContentWorkflowDialogOpener();
    private readonly showViewModuleContentWorkflowLinkService: ShowViewModuleContentWorkflowLinkService = inject(
        ShowViewModuleContentWorkflowLinkService,
    );
    private readonly workflowEntityService: WorkflowEntityService = inject(WorkflowEntityService);

    hasAnyErrors$ = this.ruleErrorStateService.hasAnyErrors$;
    hasShownErrors$ = this.ruleErrorStateService.hasShownErrors$;
    @ViewChildren('errorSummary', { read: ElementRef }) errorSummaryQuery$: QueryList<ElementRef>;
    @ViewChild(PlanStructureEditInvalidSelectionsComponent) invalidSelectionsComponent: PlanStructureEditInvalidSelectionsComponent;
    @ViewChild('freeEditMenuButton', { read: ElementRef }) freeEditMenuButtonRef: ElementRef<HTMLElement>;
    actionOptions: { action: () => void; label: string }[] = [
        { action: () => this.openFreeEditDialog(), label: 'SIS_COMPONENTS.PLAN.FREE_EDIT_MENU_OPTION' },
    ];

    data$: Observable<PlanStructureEditModalData>;
    submitClick$: Subject<void> = new Subject();
    allCustomStudyDrafts: CustomStudyDraft[] = [];
    educationOptions: GenericPlanEducationOption[] = [];
    moduleContentApplication: ModuleContentApplication;
    workflowUpdated = false;

    ngOnInit() {
        // Important to clone the validatable plan to avoid modifying the original plan
        this.planManager.setValidatablePlan(_.cloneDeep(this.modalValues.validatablePlan));
        this.planManager.setStudyRight(this.modalValues.validatablePlanStudyRight);
        this.data$ = this.createDataObservable();
        this.createPlanOperationSubjectSubscription();
        this.createPlanUIOperationSubscriptions();
        this.createSubmitClickSubscription();
    }

    ngOnDestroy() {
        // Reload plan data in case module content workflow was updated. Possible reload is done only if
        // Eiku or top right corner X is clicked, not when saving modifications
        if (this.workflowUpdated) {
            window.location.reload();
        }
    }

    createDataObservable(): Observable<PlanStructureEditModalData> {
        // First emitted value will be the initial validatable plan, after that,
        // emit values from planManager.validatablePlanSubject
        return merge(of(this.modalValues.validatablePlan), this.planManager.validatablePlanSubject)
            .pipe(
                // Prepare data for plan update, combine gradescales and validatable plan
                switchMap(validatablePlan =>
                    this.createGradeScalesByIdObservable(validatablePlan).pipe(combineLatestWith(of(validatablePlan))),
                ),
                map(([gradeScalesById, newValidatablePlan]) => this.handleValidatablePlanUpdate(gradeScalesById, newValidatablePlan)),
            )
            .pipe(
                // Resolve shown selectable course units and modules.
                // This is only done once and the initial value is passed on each emit
                combineLatestWith(this.planRuleDataService.resolvePlanRuleData(this.modalValues.validatablePlan, this.modalValues.module)),
                map(([data, planRuleData]) => ({ ...data, planRuleData }) as PlanStructureEditModalData),
            );
    }

    /**
     * Gathers all grade scale ids from plan attainments and returns them in an object with the grade scale id as the key.
     *
     * @param validatablePlan Current validatable plan.
     */
    createGradeScalesByIdObservable(validatablePlan: ValidatablePlan): Observable<{ [id: string]: any }> {
        return this.gradeScaleEntityService
            .getByIds(
                sisuChain(_.values(validatablePlan.getAllAttainments())).map('gradeScaleId').concat('sis-0-5').compact().uniq().value(),
            )
            .pipe(map(gradeScales => _.keyBy(gradeScales, 'id')));
    }

    /**
     * Subscribes to planOperationSubject and processes the operations by passing them directly to planManager.
     */
    createPlanOperationSubjectSubscription(): void {
        this.planActionsService.planOperationSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                mergeMap(operation => this.planManager.processPlanOperation(operation)),
            )
            .subscribe();
    }

    createPlanUIOperationSubscriptions(): void {
        this.planActionsService.uiOperationSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(operation => this.uiOperationHandler(operation)),
            )
            .subscribe();
    }

    uiOperationHandler(operation: UiOperation): void {
        switch (operation.uiOperationType) {
            case UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_CREATION_MODAL: {
                this.studentPlanUIActionsImplementationService
                    .openCreateCustomStudyDraftModalHandler(this.modalValues.module.id)
                    .afterClosed()
                    .subscribe(result => {
                        if (result?.operation === 'ADD' && result?.customStudyDraft) {
                            this.planActionsService.addCustomStudyDraft(result.customStudyDraft, this.modalValues.module);
                        }
                    });
                break;
            }
            case UiOperationType.OPEN_MODULE_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService.openModuleInfoModalHandler(
                    operation.target,
                    this.modalValues.validatablePlan,
                    this.modalValues.validatablePlanStudyRight,
                    this.modalValues.planValidationResult,
                );
                break;
            }
            case UiOperationType.OPEN_COURSE_UNIT_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService
                    .openCourseUnitInfoModalHandler(
                        operation.target,
                        this.modalValues.validatablePlan,
                        this.modalValues.validatablePlanStudyRight,
                    )
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe((stateChange: boolean) => {
                        if (stateChange) {
                            this.dismiss();
                        }
                    });
                break;
            }
            case UiOperationType.OPEN_CUSTOM_ATTAINMENT_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService.openCustomAttainmentInfoModalHandler(operation.target);
                break;
            }
            case UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_INFO_MODAL: {
                this.studentPlanUIActionsImplementationService.openCustomStudyDraftInfoDialogHandler(
                    operation.target,
                    this.modalValues.validatablePlan.plan.userId,
                    // Don't allow moving to application wizards from edit modal
                    { customAttainmentApplicationsDisabled: true },
                );
                break;
            }
            case UiOperationType.OPEN_ATTAINMENT_DETAILS_MODAL: {
                this.handleOpenAttainmentDetailsModalUiOperation(operation.target);
                break;
            }
        }
    }

    handleOpenAttainmentDetailsModalUiOperation(courseUnit: CourseUnit): void {
        let planStructureData: PlanStructureEditModalData;
        this.data$
            .pipe(
                take(1),
                this.appErrorHandler.defaultErrorHandler(),
                tap(data => (planStructureData = data)),
                switchMap(data => {
                    const courseUnitAttainment = data.validatablePlan.getCourseUnitAttainment(courseUnit.id);
                    const courseUnitSelection = data.validatablePlan.getCourseUnitSelection(courseUnit.id) as CourseUnitSelection;
                    this.dismiss();
                    return this.studentPlanUIActionsImplementationService.openOpenAttainmentDetailsModalHandler(
                        courseUnitAttainment,
                        courseUnitSelection?.gradeRaiseAttempt,
                    );
                }),
            )
            .subscribe({
                next: ref => {
                    ref.closed.subscribe((result: DialogActions) => {
                        if (result?.openSelectCompletionMethodModal) {
                            this.modalValues.dismissFocusTarget?.focus(); // sometimes ng-bootstrap loses focus when opening two modals immediately one after another
                            this.handleOpenSelectCompletionMethodWithGradeRaiseAttemptModalUiOperation(courseUnit, planStructureData);
                        }
                    });
                },
                error: () => {},
            });
    }

    private handleOpenSelectCompletionMethodWithGradeRaiseAttemptModalUiOperation(
        plannedCourseUnit: CourseUnit,
        data: PlanStructureEditModalData,
    ): void {
        const courseUnitSelection = data.validatablePlan.getCourseUnitSelection(plannedCourseUnit.id) as CourseUnitSelection;
        if (courseUnitSelection?.gradeRaiseAttempt && courseUnitSelection.gradeRaiseAttempt.courseUnitId !== plannedCourseUnit.id) {
            this.getCourseUnit(courseUnitSelection?.gradeRaiseAttempt.courseUnitId).subscribe((gradeRaiseAttemptCourseUnit: CourseUnit) => {
                this.openCompletionMethodModal(gradeRaiseAttemptCourseUnit, plannedCourseUnit.id, data, true);
            });
        } else {
            this.openCompletionMethodModal(plannedCourseUnit, plannedCourseUnit.id, data, true);
        }
    }

    private getCourseUnit(courseUnitId: OtmId) {
        return this.courseUnitEntityService.getById(courseUnitId).pipe(take(1), this.appErrorHandler.defaultErrorHandler());
    }

    private openCompletionMethodModal(
        courseUnit: CourseUnit,
        plannedCourseUnitId: OtmId,
        data: PlanStructureEditModalData,
        isGradeRaiseAttempt: boolean,
    ): void {
        this.studentPlanUIActionsImplementationService
            .openSelectCompletionMethodModalHandler(courseUnit, plannedCourseUnitId, data.validatablePlan, isGradeRaiseAttempt)
            .result.then(res => {
                this.planEntityService
                    .updateMyPlan(res.validatablePlan.plan)
                    .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
                    .subscribe();
            })
            .catch(this.appErrorHandler.defaultErrorHandler);
    }

    createSubmitClickSubscription() {
        this.submitClick$
            .pipe(
                withLatestFrom(this.data$),
                exhaustMap(([voidValue, data]) =>
                    this.save(data.validatablePlan)
                        // On success tap observer next will be called, on error tap is bypassed and the error is consumed by the error handler
                        .pipe(
                            switchMap(notification => {
                                if (notification === 'SUCCESS') {
                                    this.workflowUpdated = false;
                                    this.dialogService.close();
                                }
                                if (notification === 'INVALID') {
                                    if (this.errorSummaryQuery$.length === 0) {
                                        return this.errorSummaryQuery$.changes.pipe(
                                            take(1),
                                            tap(([errorSummary]) => {
                                                if (errorSummary) {
                                                    this.focusElementRef(errorSummary);
                                                }
                                            }),
                                        );
                                    }
                                    this.focusElementRef(this.errorSummaryQuery$.first);
                                }
                                return of(null);
                            }),
                            this.appErrorHandler.defaultErrorHandler(),
                        ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    focusElementRef(elRef: ElementRef) {
        elRef.nativeElement.focus();
        elRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    handleValidatablePlanUpdate(gradeScalesById: any, newValidatablePlan: ValidatablePlan): Partial<PlanStructureEditModalData> {
        const { validatablePlan, validatablePlanStudyRight, module, education } = this.modalValues;
        const planValidationResult = PlanValidationTs.validatePlan(newValidatablePlan);
        this.educationOptions = PlanStudyRightService.getValidatedEducationOptions(validatablePlan, education, validatablePlanStudyRight);
        const selectionPathInPlan = PlanStudyRightService.getSelectionPathInPlan(validatablePlan, education, null);
        const planStateAndData = this.planStateService.getPlanStateAndData(
            education,
            newValidatablePlan,
            planValidationResult,
            this.educationOptions,
            gradeScalesById,
            validatablePlanStudyRight,
        );
        this.collectCustomStudyDrafts(newValidatablePlan);
        return {
            planData: planStateAndData.planData,
            planStateObject: planStateAndData.planStateObject,
            planValidationResult,
            validatablePlan: newValidatablePlan,
            selectedModule: module,
        };
    }

    focusRule([ruleErrorState]: [RuleErrorState, RuleError]) {
        const el = document.getElementById(ruleErrorState.ruleFocusId);
        if (el) {
            el.focus();
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    collectCustomStudyDrafts(validatablePlan: ValidatablePlan) {
        const currentCustomStudyDrafts = validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(this.modalValues.module.id);
        const allCustomStudyDrafts = _.concat(this.allCustomStudyDrafts, currentCustomStudyDrafts);
        this.allCustomStudyDrafts = _.uniqBy(_.compact(allCustomStudyDrafts), 'id');
    }

    dismiss() {
        this.dialogService.close();
    }

    save(newValidatablePlan: ValidatablePlan): Observable<'SUCCESS' | 'INVALID'> {
        if (this.hasAnyErrors$()) {
            this.ruleErrorStateService.showCurrentErrorStates();
            return of('INVALID');
        }
        let customStudyDraftConfirmationObservable: Observable<boolean> = of(true);
        if (
            this.planDiffService.hasUnselectedCustomStudyDrafts(
                this.allCustomStudyDrafts,
                newValidatablePlan.getSelectedCustomStudyDraftsByParentModuleId(this.modalValues.module.id),
            )
        ) {
            customStudyDraftConfirmationObservable = this.confirmDialogFdOpener({
                title: 'PLAN.CUSTOM_STUDY_DRAFT_DELETE_CONFIRM_MODAL.TITLE',
                descriptions: [
                    'PLAN.CUSTOM_STUDY_DRAFT_DELETE_CONFIRM_MODAL.DESCRIPTION_1',
                    'PLAN.CUSTOM_STUDY_DRAFT_DELETE_CONFIRM_MODAL.DESCRIPTION_2',
                ],
                confirmObservable: of(true),
            } as ConfirmDialogFdValues<boolean>).afterClosed();
        }
        const planUpdateObservable = this.planEntityService.updateMyPlan(newValidatablePlan.plan).pipe(
            take(1),
            map(() => 'SUCCESS'),
        ) as Observable<'SUCCESS'>;
        return customStudyDraftConfirmationObservable.pipe(switchMap((result: boolean) => (result ? planUpdateObservable : EMPTY)));
    }

    protected handleFocusClick(event: Event): void {
        event.preventDefault();

        this.scrollService.scrollToElementById('invalid-selections-title');
    }

    showLinkToStudyPath(): boolean {
        // Show the link only, when opened from Education and DegreeProgramme modules
        return (
            this.modalValues.validatablePlanStudyRight &&
            (this.modalValues.module.type === 'Education' || this.modalValues.module.type === 'DegreeProgramme')
        );
    }

    getLinkToStudyPathClass(): string {
        if (this.invalidSelectionsComponent?.hasInvalidSelections) {
            return 'fudis-mt-xl';
        }
    }

    hasInvalidSelections(): boolean {
        return !!(
            this.invalidSelectionsComponent?.hasInvalidSelections &&
            this.showViewModuleContentWorkflowLinkService.planHasActiveStudyRight(
                this.modalValues.validatablePlan,
                this.modalValues.allStudentStudyRights,
            ) &&
            !this.moduleContentApplicationRequested() &&
            !this.moduleContentApplicationAccepted() &&
            !this.moduleContentApplicationChanged() &&
            !this.moduleContentApplicationConditionallyAccepted()
        );
    }

    moduleContentApplicationRequested(): boolean {
        return (
            this.newestModuleContentApplication()?.state === 'REQUESTED' || this.newestModuleContentApplication()?.state === 'IN_HANDLING'
        );
    }

    moduleContentApplicationAccepted(): boolean {
        return this.newestModuleContentApplication()?.state === 'ACCEPTED';
    }

    moduleContentApplicationConditionallyAccepted(): boolean {
        return this.newestModuleContentApplication()?.state === 'CONDITIONAL';
    }

    moduleContentApplicationChanged(): boolean {
        let applicationChanged = false;
        this.data$.pipe(take(1)).subscribe(data => {
            const planStateObject = data.planStateObject;
            const courseUnits = this.modalValues.validatablePlan.getSelectedCourseUnitsUnderModule(this.modalValues.module);
            applicationChanged = courseUnits.some(courseUnit => {
                const state = _.get(planStateObject?.courseUnits, courseUnit.id);
                return (
                    state &&
                    (state.invalidSelectionAccordingToModuleContentApproval ||
                        state.isInvalidAccordingToModuleContentApprovalRequested ||
                        state.isInvalidAccordingToModuleContentApprovalAccepted)
                );
            });
        });
        return (
            applicationChanged &&
            (this.moduleContentApplicationAccepted() ||
                this.moduleContentApplicationRequested() ||
                this.moduleContentApplicationConditionallyAccepted())
        );
    }

    isModuleContentApplicationRequired(data: PlanStructureEditModalData) {
        const selectedModule = data.selectedModule as Module;
        if (selectedModule) {
            return this.showViewModuleContentWorkflowLinkService.isModuleContentApplicationRequired(selectedModule);
        }
        return false;
    }

    showApplyForModuleContentWorkflowRequiredDialog(data: PlanStructureEditModalData) {
        if (
            this.hasStudyRightForStudyPlan(data) &&
            this.hasSelectedModuleValidState() &&
            !this.moduleContentApplicationRequested() &&
            !this.moduleContentApplicationAccepted() &&
            !this.moduleContentApplicationConditionallyAccepted()
        ) {
            return this.isModuleContentApplicationRequired(data);
        }
        return false;
    }

    hasStudyRightForStudyPlan(data: PlanStructureEditModalData): boolean {
        return this.showViewModuleContentWorkflowLinkService.planHasActiveStudyRight(
            data.validatablePlan,
            this.modalValues.allStudentStudyRights,
        );
    }

    hasSelectedModuleValidState() {
        return this.showViewModuleContentWorkflowLinkService.isModuleStateValid(
            this.modalValues.planValidationResult,
            this.modalValues.module?.id,
        );
    }

    // The module content workflow application must still reference the study right it was created under even if that study
    // right has since ended (e.g. EXPIRED), so this bypasses the "currently valid" restriction of validatablePlanStudyRight.
    getStudyRightForModuleContentWorkflowApplication(): StudyRight {
        return findMatchingStudyRightIgnoringState(this.modalValues.validatablePlan.plan, this.modalValues.allStudentStudyRights);
    }

    openModuleContentWorkflowDialog(): void {
        this.data$.pipe(take(1)).subscribe(data => {
            const selectedModule = data.selectedModule as Module;
            const planData = data.planData;
            const planStateObject = data.planStateObject;

            this.moduleContentWorkflowDialogOpener({
                approvalRequestType: this.showViewModuleContentWorkflowLinkService.isModuleContentApplicationRequired(selectedModule)
                    ? 'REQUIRED'
                    : 'CUSTOM',
                module: selectedModule,
                validatablePlan: this.modalValues.validatablePlan,
                matchingStudyRight: this.getStudyRightForModuleContentWorkflowApplication(),
                planData,
                planStateObject,
            })
                .afterClosed()
                .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
                .subscribe((workflow: ModuleContentWorkflow) => {
                    if (workflow) {
                        this.moduleContentApplication = {
                            ...workflow,
                            type:
                                workflow.type === 'CustomModuleContentWorkflow'
                                    ? 'CustomModuleContentApplication'
                                    : 'RequiredModuleContentApplication',
                        };
                        this.modalValues.validatablePlan.moduleContentApplications.push(this.moduleContentApplication);
                        this.changeDetectorRef.detectChanges();
                        this.invalidSelectionsComponent.refresh();
                        this.workflowUpdated = true;
                    }
                });
        });
    }

    openApplyForModuleContentWorkflowDialog(data: PlanStructureEditModalData): void {
        this.applyForModuleContentWorkflowDialogOpener({
            approvalRequestType: this.showViewModuleContentWorkflowLinkService.isModuleContentApplicationRequired(
                data.selectedModule as Module,
            )
                ? 'REQUIRED'
                : 'CUSTOM',
            module: data.selectedModule as Module,
            validatablePlan: this.modalValues.validatablePlan,
            matchingStudyRight: this.getStudyRightForModuleContentWorkflowApplication(),
            planData: data.planData,
            planStateObject: data.planStateObject,
        })
            .afterClosed()
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe((workflow: ModuleContentWorkflow) => {
                if (workflow) {
                    this.workflowCreated(workflow);
                    this.changeDetectorRef.detectChanges();
                    this.workflowUpdated = true;
                }
            });
    }

    workflowCreated(workflow: ModuleContentWorkflow) {
        this.moduleContentApplication = {
            ...workflow,
            type: workflow.type === 'CustomModuleContentWorkflow' ? 'CustomModuleContentApplication' : 'RequiredModuleContentApplication',
        };
        this.modalValues.validatablePlan.moduleContentApplications.push(this.moduleContentApplication);
        this.invalidSelectionsComponent.refresh();
        this.workflowUpdated = true;
    }

    private newestModuleContentApplication(): ModuleContentApplication {
        return this.modalValues.validatablePlan.moduleContentApplications
            .slice()
            .sort((a, b) => new Date(a.metadata.lastModifiedOn).getTime() - new Date(b.metadata.lastModifiedOn).getTime())
            .findLast(app =>
                this.workflowEntityService.isMatchingModuleContentWorkflow(
                    app,
                    this.modalValues.validatablePlan,
                    this.modalValues.module.id,
                ),
            );
    }

    openFreeEditDialog() {
        this.dialogService
            .open(PlanStructureFreeEditComponent, {
                data: {
                    ...this.modalValues,
                    handleOpenAttainmentDetailsModalUiOperation: this.handleOpenAttainmentDetailsModalUiOperation.bind(this),
                },
            })
            .afterClosed()
            .subscribe(() => {
                // The dialog's triggering element (a fudis-dropdown-menu-item) is removed from the DOM as soon as the
                // dropdown closes, so MatDialog's automatic restoreFocus has nothing to focus once this dialog closes.
                // Move focus back to the still-present menu button that opened it instead.
                this.freeEditMenuButtonRef?.nativeElement.querySelector('button')?.focus();
            });
    }
}
